import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_URL } from '../../config/apiConfig';
import { FaSave, FaSpinner, FaPlus, FaTrash } from 'react-icons/fa';

const SupportDetails = () => {
  const [support, setSupport] = useState({
    companyName: 'CabZigo',
    description: '',
    contactChannels: [],
    workingHours: [],
    socialMedia: [],
    officeLocations: [],
    emergencyContact: '',
    website: '',
    chatLink: ''
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [newChannel, setNewChannel] = useState({ type: 'email', value: '', label: '' });
  const [newWorkingHour, setNewWorkingHour] = useState({
    day: 'Monday',
    open: '09:00',
    close: '18:00',
    isClosed: false
  });
  const [newSocial, setNewSocial] = useState({ platform: 'facebook', url: '', handle: '' });
  const [newLocation, setNewLocation] = useState({
    city: '',
    address: '',
    phone: '',
    email: '',
    coordinates: { latitude: 0, longitude: 0 }
  });

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const channelTypes = ['email', 'phone', 'whatsapp', 'telegram', 'live-chat', 'address'];
  const platforms = ['facebook', 'twitter', 'instagram', 'linkedin', 'youtube'];

  useEffect(() => {
    fetchSupportDetails();
  }, []);

  const fetchSupportDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/content/public/support-details`);
      if (response.data.success) {
        setSupport(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching support details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSupport((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const addContactChannel = () => {
    if (!newChannel.type || !newChannel.value) {
      toast.error('Please fill in all fields');
      return;
    }
    setSupport((prev) => ({
      ...prev,
      contactChannels: [...prev.contactChannels, { ...newChannel, isActive: true }]
    }));
    setNewChannel({ type: 'email', value: '', label: '' });
  };

  const removeContactChannel = (index) => {
    setSupport((prev) => ({
      ...prev,
      contactChannels: prev.contactChannels.filter((_, i) => i !== index)
    }));
  };

  const addWorkingHour = () => {
    if (!newWorkingHour.day || !newWorkingHour.open || !newWorkingHour.close) {
      toast.error('Please fill in all fields');
      return;
    }
    setSupport((prev) => ({
      ...prev,
      workingHours: [...prev.workingHours, newWorkingHour]
    }));
    setNewWorkingHour({ day: 'Monday', open: '09:00', close: '18:00', isClosed: false });
  };

  const removeWorkingHour = (index) => {
    setSupport((prev) => ({
      ...prev,
      workingHours: prev.workingHours.filter((_, i) => i !== index)
    }));
  };

  const addSocialMedia = () => {
    if (!newSocial.platform || !newSocial.url) {
      toast.error('Please fill in all fields');
      return;
    }
    setSupport((prev) => ({
      ...prev,
      socialMedia: [...prev.socialMedia, newSocial]
    }));
    setNewSocial({ platform: 'facebook', url: '', handle: '' });
  };

  const removeSocialMedia = (index) => {
    setSupport((prev) => ({
      ...prev,
      socialMedia: prev.socialMedia.filter((_, i) => i !== index)
    }));
  };

  const addOfficeLocation = () => {
    if (!newLocation.city || !newLocation.address) {
      toast.error('Please fill in city and address');
      return;
    }
    setSupport((prev) => ({
      ...prev,
      officeLocations: [...prev.officeLocations, newLocation]
    }));
    setNewLocation({ city: '', address: '', phone: '', email: '', coordinates: { latitude: 0, longitude: 0 } });
  };

  const removeOfficeLocation = (index) => {
    setSupport((prev) => ({
      ...prev,
      officeLocations: prev.officeLocations.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!support.companyName) {
      toast.error('Company name is required');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/content/admin/support-details`, support, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Support details updated successfully');
        setSupport(response.data.data);
      }
    } catch (error) {
      console.error('Error updating support details:', error);
      toast.error(error.response?.data?.message || 'Failed to update support details');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center min-h-96">
        <FaSpinner className="animate-spin text-3xl text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Support Details</h1>
        <p className="text-gray-600">Manage company contact information and support channels</p>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-4">Basic Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                <input
                  type="text"
                  value={support.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                <input
                  type="text"
                  value={support.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  placeholder="https://example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={support.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Company description and support overview..."
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Emergency Contact</label>
                <input
                  type="text"
                  value={support.emergencyContact}
                  onChange={(e) => handleInputChange('emergencyContact', e.target.value)}
                  placeholder="+1234567890"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Support Chat Link (tawk.to, WhatsApp, etc.)</label>
                <input
                  type="text"
                  value={support.chatLink}
                  onChange={(e) => handleInputChange('chatLink', e.target.value)}
                  placeholder="https://tawk.to/chat/..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Contact Channels */}
          <div className="border-t pt-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Contact Channels</h2>
            <div className="bg-gray-50 p-4 rounded-lg mb-4 space-y-3">
              <select
                value={newChannel.type}
                onChange={(e) => setNewChannel({ ...newChannel, type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {channelTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={newChannel.value}
                onChange={(e) => setNewChannel({ ...newChannel, value: e.target.value })}
                placeholder="Value (e.g., contact@example.com, +1234567890)"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                value={newChannel.label}
                onChange={(e) => setNewChannel({ ...newChannel, label: e.target.value })}
                placeholder="Label (optional, e.g., 'Support Email')"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={addContactChannel}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                <FaPlus /> Add Channel
              </button>
            </div>

            {support.contactChannels.length > 0 && (
              <div className="space-y-2">
                {support.contactChannels.map((channel, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg flex justify-between items-center">
                    <div>
                      <p className="font-semibold text-gray-800">{channel.type.toUpperCase()}</p>
                      <p className="text-gray-600">{channel.value}</p>
                      {channel.label && <p className="text-gray-500 text-sm">{channel.label}</p>}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeContactChannel(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Working Hours */}
          <div className="border-t pt-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Working Hours</h2>
            <div className="bg-gray-50 p-4 rounded-lg mb-4 space-y-3">
              <select
                value={newWorkingHour.day}
                onChange={(e) => setNewWorkingHour({ ...newWorkingHour, day: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {days.map((day) => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="time"
                  value={newWorkingHour.open}
                  onChange={(e) => setNewWorkingHour({ ...newWorkingHour, open: e.target.value })}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="time"
                  value={newWorkingHour.close}
                  onChange={(e) => setNewWorkingHour({ ...newWorkingHour, close: e.target.value })}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newWorkingHour.isClosed}
                  onChange={(e) => setNewWorkingHour({ ...newWorkingHour, isClosed: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm text-gray-700">Closed on this day</span>
              </label>
              <button
                type="button"
                onClick={addWorkingHour}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                <FaPlus /> Add Working Hours
              </button>
            </div>

            {support.workingHours.length > 0 && (
              <div className="space-y-2">
                {support.workingHours.map((hour, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg flex justify-between items-center">
                    <div>
                      <p className="font-semibold text-gray-800">{hour.day}</p>
                      {hour.isClosed ? (
                        <p className="text-gray-600">Closed</p>
                      ) : (
                        <p className="text-gray-600">{hour.open} - {hour.close}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeWorkingHour(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Social Media */}
          <div className="border-t pt-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Social Media</h2>
            <div className="bg-gray-50 p-4 rounded-lg mb-4 space-y-3">
              <select
                value={newSocial.platform}
                onChange={(e) => setNewSocial({ ...newSocial, platform: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {platforms.map((platform) => (
                  <option key={platform} value={platform}>
                    {platform.charAt(0).toUpperCase() + platform.slice(1)}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={newSocial.url}
                onChange={(e) => setNewSocial({ ...newSocial, url: e.target.value })}
                placeholder="Profile URL"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                value={newSocial.handle}
                onChange={(e) => setNewSocial({ ...newSocial, handle: e.target.value })}
                placeholder="Handle (e.g., @cabzigo)"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={addSocialMedia}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                <FaPlus /> Add Social Media
              </button>
            </div>

            {support.socialMedia.length > 0 && (
              <div className="space-y-2">
                {support.socialMedia.map((social, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg flex justify-between items-center">
                    <div>
                      <p className="font-semibold text-gray-800">{social.platform.toUpperCase()}</p>
                      <p className="text-gray-600">{social.url}</p>
                      {social.handle && <p className="text-gray-500 text-sm">{social.handle}</p>}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSocialMedia(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Office Locations */}
          <div className="border-t pt-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Office Locations</h2>
            <div className="bg-gray-50 p-4 rounded-lg mb-4 space-y-3">
              <input
                type="text"
                value={newLocation.city}
                onChange={(e) => setNewLocation({ ...newLocation, city: e.target.value })}
                placeholder="City"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                value={newLocation.address}
                onChange={(e) => setNewLocation({ ...newLocation, address: e.target.value })}
                placeholder="Address"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                value={newLocation.phone}
                onChange={(e) => setNewLocation({ ...newLocation, phone: e.target.value })}
                placeholder="Phone (optional)"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                value={newLocation.email}
                onChange={(e) => setNewLocation({ ...newLocation, email: e.target.value })}
                placeholder="Email (optional)"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={addOfficeLocation}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                <FaPlus /> Add Location
              </button>
            </div>

            {support.officeLocations.length > 0 && (
              <div className="space-y-2">
                {support.officeLocations.map((location, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg flex justify-between items-center">
                    <div>
                      <p className="font-semibold text-gray-800">{location.city}</p>
                      <p className="text-gray-600">{location.address}</p>
                      {location.phone && <p className="text-gray-600">{location.phone}</p>}
                      {location.email && <p className="text-gray-600">{location.email}</p>}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeOfficeLocation(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex gap-4 pt-6 border-t">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {saving ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FaSave />
                  Save Details
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupportDetails;
